import axios from 'axios'
import { createContext, type ReactNode, useCallback, useEffect, useState } from 'react'

import { DIALPAD_CLIENT_ID } from '../../config/config'
import type { DialpadRingingCall, DialpadStatus } from './types'

const WIDGET_CONTAINER_ID = 'dialpad-cti-iframe'
const DIALPAD_ORIGIN = 'https://dialpad.com'

export interface DialpadContextValue {
  status: DialpadStatus
  /** True once the user has signed into the embedded dialer. */
  isLoggedIn: boolean
  ringingCall: DialpadRingingCall | null
  expanded: boolean
  /** https://dialpad.com/apps/<clientId>; empty when status is 'disabled'. */
  iframeSrc: string
  /** id of the dialer iframe. Render once in the DialpadProvider tree. */
  widgetContainerId: string
  dial: (args: { to: string; leadId?: string; contactId?: string }) => Promise<void>
  expand: () => void
  collapse: () => void
}

export const DialpadContext = createContext<DialpadContextValue | null>(null)

interface DialpadMessage {
  api?: string
  method?: string
  payload?: Record<string, unknown>
}

/**
 * Direction of a `call_ringing` payload, or null when Dialpad didn't say. It has
 * sent this as both an explicit `direction` and an `is_inbound` boolean; anything
 * else stays null so the caller can't mistake an outbound dial for a ring.
 */
function ringDirection(payload: Record<string, unknown>): string | null {
  const direction = payload.direction
  if (direction === 'inbound' || direction === 'outbound') return direction
  if (typeof payload.is_inbound === 'boolean') return payload.is_inbound ? 'inbound' : 'outbound'
  return null
}

/**
 * Wraps the internal layout and exposes Dialpad dialer state. No backend, no
 * SDK package — just an iframe (rendered by DialpadWidget) and the postMessage
 * channel the Mini Dialer uses to report login + inbound-call state.
 */
export function DialpadProvider({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [ringingCall, setRingingCall] = useState<DialpadRingingCall | null>(null)

  const status: DialpadStatus = DIALPAD_CLIENT_ID ? 'ready' : 'disabled'

  useEffect(() => {
    if (status !== 'ready') return

    function onMessage(event: MessageEvent) {
      // The dialer iframe is the only frame allowed to drive our state.
      if (event.origin !== DIALPAD_ORIGIN) return
      const data = event.data as DialpadMessage | null
      if (!data || data.api !== 'opencti_dialpad') return

      if (data.method === 'user_authentication') {
        setIsLoggedIn(Boolean(data.payload?.user_authenticated))
        return
      }
      if (data.method === 'call_ringing') {
        const payload = data.payload ?? {}
        if (payload.state === 'on') {
          const contact = (payload.contact ?? {}) as { phone?: string; name?: string }
          setRingingCall({
            from: contact.phone ?? (payload.external_number as string | undefined) ?? '',
            name: contact.name ?? null,
            direction: ringDirection(payload),
          })
          // Surface the incoming call without the user having to open the pill.
          setExpanded(true)
        } else {
          setRingingCall(null)
        }
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [status])

  const dial = useCallback(
    async ({ to, leadId, contactId }: { to: string; leadId?: string; contactId?: string }) => {
      if (status !== 'ready') throw new Error('Dialpad not ready')
      if (leadId) {
        await axios.post('/api/v1/insure/telephony/dial/', {
          to,
          lead_id: leadId,
          contact_id: contactId,
        })
      }
      const iframe = document.getElementById(WIDGET_CONTAINER_ID) as HTMLIFrameElement | null
      iframe?.contentWindow?.postMessage(
        { api: 'opencti_dialpad', version: '1.0', method: 'initiate_call', payload: { phone_number: to } },
        DIALPAD_ORIGIN
      )
      setExpanded(true)
    },
    [status]
  )

  const expand = useCallback(() => setExpanded(true), [])
  const collapse = useCallback(() => setExpanded(false), [])

  const value: DialpadContextValue = {
    status,
    isLoggedIn,
    ringingCall,
    expanded,
    iframeSrc: status === 'ready' ? `${DIALPAD_ORIGIN}/apps/${DIALPAD_CLIENT_ID}` : '',
    widgetContainerId: WIDGET_CONTAINER_ID,
    dial,
    expand,
    collapse,
  }

  return <DialpadContext.Provider value={value}>{children}</DialpadContext.Provider>
}
